import React from 'react';
import { Input, Button, Form, Card, Empty, message, DatePicker, Select, Space, Upload, List, Tabs, Modal, Table, InputNumber, Spin } from 'antd';
import { FormInstance } from 'antd/lib/form';
import moment from 'moment';
import { UploadOutlined, PlusOutlined, MinusCircleOutlined, DragOutlined } from '@ant-design/icons';
import { DragDropContext, Draggable, Droppable, DropResult } from 'react-beautiful-dnd';
import BreadcrumbCustom from '../../components/widget/BreadcrumbCustom';
import ProjectService, { IAiPlanPreviewStage, IProjectAttachment, IProjectInfo, IProjectStage, IProjectStageOperationHistory, ProjectSaveParam, ProjectUpdateParam } from '../../service/project/ProjectService';
import { SearchSelectOption } from '../../components/public/SearchSelectComponent';
import ProjectManagerSelect from '../../components/public/ProjectManagerSelect';
import ProjectStatusSelect, { ProjectStatus } from '../../components/public/ProjectStatusSelect';
import ProjectStageStatusSelect from '../../components/public/ProjectStageStatusSelect';
import { PROJECT_SERVICE_CATEGORY_OPTIONS } from './project-constants';
import {
    ADD_PROJECT_ERROR_MESSAGE,
    ADD_PROJECT_SUCCESS_MESSAGE,
    DELETE_PROJECT_STAGE_CONFIRM_MESSAGE,
    EDIT_PROJECT_ERROR_MESSAGE,
    EDIT_PROJECT_SUCCESS_MESSAGE,
    UPLOAD_PROJECT_ATTACHMENT_ERROR_MESSAGE,
    UPLOAD_PROJECT_ATTACHMENT_SUCCESS_MESSAGE,
} from '../../config/strcfg';
import './page-project.less';
import { buildApiUrl, buildFileUrl, triggerDownload } from '../../utils/fileTools';

const FIXED_COST_CLIENT_HOSPITALITY = '客户招待';

interface ProjectManagerPageState {
    loading: boolean;
    aiImportLoading: boolean;
    isEditMode: boolean;
    data: IProjectInfo | null;
    attachmentList: IProjectAttachment[];
    companyOptions: SearchSelectOption[];
    customerManagerOptions: SearchSelectOption[];
    stageExecutorOptions: string[];
    calculatedProjectProfit: string;
    calculatedProfitRate: string;
    showIncomeMismatchFields: boolean;
    aiPreviewVisible: boolean;
    aiPreviewProjectTarget: string;
    aiPreviewStageList: IAiPlanPreviewStage[];
    aiConfirmingPreviewIndex: number | null;
    stageHistoryVisible: boolean;
    stageHistoryLoading: boolean;
    stageOperationHistoryList: IProjectStageOperationHistory[];
}

class ProjectManagerPage extends React.Component<any, ProjectManagerPageState> {
    state: ProjectManagerPageState = {
        loading: false,
        aiImportLoading: false,
        isEditMode: false,
        data: null,
        attachmentList: [],
        companyOptions: [],
        customerManagerOptions: [],
        stageExecutorOptions: [],
        calculatedProjectProfit: '0.00',
        calculatedProfitRate: '0.00',
        showIncomeMismatchFields: false,
        aiPreviewVisible: false,
        aiPreviewProjectTarget: '',
        aiPreviewStageList: [],
        aiConfirmingPreviewIndex: null,
        stageHistoryVisible: false,
        stageHistoryLoading: false,
        stageOperationHistoryList: [],
    };

    formRef = React.createRef<FormInstance>();

    isViewMode = () => window.location.hash.startsWith('#/app/project/view');

    parseNumber = (value: any): number => {
        if (value === null || value === undefined || value === '') {
            return 0;
        }
        const num = Number(value);
        return Number.isFinite(num) ? num : 0;
    };

    sanitizeAmountInput = (input: any): string => {
        const value = (input || '').toString();
        const noInvalidChars = value.replace(/[^\d.]/g, '');
        const normalized = noInvalidChars
            .replace(/^\./, '')
            .replace(/\.{2,}/g, '.')
            .replace('.', '$#$')
            .replace(/\./g, '')
            .replace('$#$', '.');
        return normalized;
    };

    parseOtherCostItems = (otherCostItems?: string | null): Array<{ name?: string; amount?: number }> => {
        if (!otherCostItems) {
            return [];
        }
        try {
            const list = JSON.parse(otherCostItems);
            if (Array.isArray(list)) {
                return list.map(item => ({
                    name: item?.name || '',
                    amount: this.parseNumber(item?.amount),
                }));
            }
        } catch (e) {
            // fall through
        }
        return [];
    };

    getInitialClientHospitalityCost = (data: IProjectInfo): number => {
        const parsed = this.parseOtherCostItems(data.otherCostItems);
        const fixedItem = parsed.find(item => (item?.name || '').trim() === FIXED_COST_CLIENT_HOSPITALITY);
        return fixedItem ? this.parseNumber(fixedItem.amount) : 0;
    };

    buildInitialOtherCostItems = (data: IProjectInfo): Array<{ name?: string; amount?: number }> => {
        const parsed = this.parseOtherCostItems(data.otherCostItems);
        if (parsed.length > 0) {
            return parsed.filter(item => (item?.name || '').trim() !== FIXED_COST_CLIENT_HOSPITALITY);
        }

        const legacyItems = [
            { name: '其他A', amount: this.parseNumber(data.costOtherA) },
            { name: '其他B', amount: this.parseNumber(data.costOtherB) },
            { name: '其他C', amount: this.parseNumber(data.costOtherC) },
        ].filter(item => item.amount !== 0);

        return legacyItems;
    };

    stringifyOtherCostItems = (otherCostItemList?: Array<{ name?: string; amount?: any }>, clientHospitalityAmount?: any): string | undefined => {
        const list = (otherCostItemList || [])
            .map(item => ({
                name: (item?.name || '').trim(),
                amount: this.parseNumber(item?.amount),
            }))
            .filter(item => !!item.name && item.name !== FIXED_COST_CLIENT_HOSPITALITY);

        const fixedAmount = this.parseNumber(clientHospitalityAmount);
        if (fixedAmount !== 0) {
            list.unshift({
                name: FIXED_COST_CLIENT_HOSPITALITY,
                amount: fixedAmount,
            });
        }

        return JSON.stringify(list);
    };

    calculateAndSetProfitInfo = (values: any) => {
        const income = this.parseNumber(values.actualIncome);
        const baseCost = [
            values.costConsultantFee,
            values.costConsultantTravel,
            values.costConsultantMeal,
            values.costMaterial,
            values.costTeachingAid,
            values.costTeaBreak,
            values.costClientHospitality,
            values.costPrize,
            values.costPmTravel,
            values.costVat,
        ].reduce((sum, item) => sum + this.parseNumber(item), 0);

        const otherCostTotal = (values.otherCostItemList || [])
            .reduce((sum: number, item: any) => sum + this.parseNumber(item?.amount), 0);

        const profit = income - baseCost - otherCostTotal;
        const rate = income !== 0 ? (profit / income) * 100 : 0;

        const showIncomeMismatchFields = values.actualIncome !== undefined
            && values.actualIncome !== ''
            && values.contractAmount !== undefined
            && values.contractAmount !== ''
            && this.parseNumber(values.actualIncome) !== this.parseNumber(values.contractAmount);

        this.setState({
            calculatedProjectProfit: profit.toFixed(2),
            calculatedProfitRate: rate.toFixed(2),
            showIncomeMismatchFields,
        });
    };

    calculateFromProjectData = (data: IProjectInfo) => {
        this.calculateAndSetProfitInfo({
            actualIncome: data.actualIncome,
            contractAmount: data.contractAmount,
            costConsultantFee: data.costConsultantFee,
            costConsultantTravel: data.costConsultantTravel,
            costConsultantMeal: data.costConsultantMeal,
            costMaterial: data.costMaterial,
            costTeachingAid: data.costTeachingAid,
            costTeaBreak: data.costTeaBreak,
            costClientHospitality: this.getInitialClientHospitalityCost(data),
            costPrize: data.costPrize,
            costPmTravel: data.costPmTravel,
            costVat: data.costVat,
            otherCostItemList: this.buildInitialOtherCostItems(data),
        });
    };

    ensureCurrentCustomerManagerOption = (data: IProjectInfo) => {
        if (!data.customerManagerId || !data.customerManagerName) {
            return;
        }
        this.setState((prevState) => {
            const exists = (prevState.customerManagerOptions || [])
                .some(option => Number(option.key) === Number(data.customerManagerId));
            if (exists) {
                return null;
            }
            return {
                customerManagerOptions: [
                    ...prevState.customerManagerOptions,
                    { key: data.customerManagerId, value: data.customerManagerName },
                ],
            };
        });
    };

    ensureStageExecutorOptions = (stageList: IProjectStage[]) => {
        const stageExecutorNames = (stageList || [])
            .map(item => item.executorDisplayName || item.executorName)
            .filter((name): name is string => !!name && !!name.trim())
            .map(name => name.trim());
        if (stageExecutorNames.length === 0) {
            return;
        }
        this.setState(prevState => ({
            stageExecutorOptions: Array.from(new Set([...(prevState.stageExecutorOptions || []), ...stageExecutorNames])),
        }));
    };

    async componentDidMount() {
        await this.loadOptions();

        const searchParams = new URLSearchParams(window.location.hash.split('?')[1]);
        const id = parseInt(searchParams.get('id') || '0');

        if (id) {
            this.setState({ isEditMode: true, loading: true });
            const response = await ProjectService.getProjectById(id);
            if (response && response.data && response.data.projectInfo) {
                const data: IProjectInfo = response.data.projectInfo;
                const stageList = data.stageList || [];
                const attachmentList = data.attachmentList || [];
                this.ensureCurrentCustomerManagerOption(data);
                this.ensureStageExecutorOptions(stageList);
                this.setState({ data, attachmentList, loading: false });
                this.formRef.current?.setFieldsValue({
                    name: data.name,
                    startDate: data.startDate ? moment(data.startDate) : undefined,
                    endDate: data.endDate ? moment(data.endDate) : undefined,
                    serviceCategory: data.serviceCategory,
                    companyId: data.companyId,
                    companyName: data.companyName,
                    customerManagerId: data.customerManagerId,
                    customerManagerName: data.customerManagerName,
                    projectManagerAdminId: data.projectManagerAdminId,
                    contractAmount: data.contractAmount,
                    status: data.status,
                    actualIncome: data.actualIncome,
                    costConsultantFee: data.costConsultantFee,
                    costConsultantTravel: data.costConsultantTravel,
                    costConsultantMeal: data.costConsultantMeal,
                    costMaterial: data.costMaterial,
                    costTeachingAid: data.costTeachingAid,
                    costTeaBreak: data.costTeaBreak,
                    costPrize: data.costPrize,
                    costPmTravel: data.costPmTravel,
                    costVat: data.costVat,
                    costClientHospitality: this.getInitialClientHospitalityCost(data),
                    otherCostItemList: this.buildInitialOtherCostItems(data),
                    incomeMismatchReason: data.incomeMismatchReason,
                    incomeMismatchRemark: data.incomeMismatchRemark,
                    projectTarget: data.projectTarget,
                    onsiteFeedbackStudent: data.onsiteFeedbackStudent,
                    onsiteFeedbackClient: data.onsiteFeedbackClient,
                    resultFeedbackSponsor: data.resultFeedbackSponsor,
                    resultTeamChange: data.resultTeamChange,
                    resultBusinessChange: data.resultBusinessChange,
                    stageList: stageList.map(item => ({
                        id: item.id,
                        stageNo: item.stageNo,
                        stageName: item.stageName,
                        implementationTime: item.implementationTime ? moment(item.implementationTime, 'YYYY-MM-DD') : undefined,
                        implementationContent: item.implementationContent,
                        executorName: item.executorDisplayName || item.executorName,
                        deliverable: item.deliverable,
                        paymentPlan: item.paymentPlan,
                        status: item.status,
                    })),
                });
                this.calculateFromProjectData(data);
            } else {
                this.setState({ loading: false });
                message.error(response ? response.message : '获取项目信息失败');
            }
        }
    }

    loadOptions = async () => {
        const baseOptionsResponse = await ProjectService.getProjectBaseOptions();
        if (baseOptionsResponse && baseOptionsResponse.data) {
            const companyOptions = (baseOptionsResponse.data.companyOptions || [])
                .map((item: any) => ({ key: item.id, value: item.name }));
            const customerManagerOptions = (baseOptionsResponse.data.customerManagerOptions || [])
                .map((item: any) => ({ key: item.id, value: item.name }));
            this.setState({ companyOptions, customerManagerOptions });
        }

        const stageExecutorResponse = await ProjectService.getStageExecutorOptions();
        if (stageExecutorResponse && stageExecutorResponse.data && Array.isArray(stageExecutorResponse.data.stageExecutorOptions)) {
            this.setState({ stageExecutorOptions: stageExecutorResponse.data.stageExecutorOptions });
        }
    };

    handleBackButtonClick = () => {
        window.history.back();
    };

    buildProjectPayload = (params: any): Partial<IProjectInfo> => ({
        name: params.name,
        startDate: params.startDate ? params.startDate.format('YYYY-MM-DD') : undefined,
        endDate: params.endDate ? params.endDate.format('YYYY-MM-DD') : undefined,
        serviceCategory: params.serviceCategory,
        companyId: params.companyId,
        customerManagerId: params.customerManagerId,
        projectManagerAdminId: params.projectManagerAdminId,
        contractAmount: params.contractAmount,
        status: params.status,
        actualIncome: params.actualIncome,
        costConsultantFee: params.costConsultantFee,
        costConsultantTravel: params.costConsultantTravel,
        costConsultantMeal: params.costConsultantMeal,
        costMaterial: params.costMaterial,
        costTeachingAid: params.costTeachingAid,
        costTeaBreak: params.costTeaBreak,
        costPrize: params.costPrize,
        costPmTravel: params.costPmTravel,
        costVat: params.costVat,
        costOtherA: 0,
        costOtherB: 0,
        costOtherC: 0,
        otherCostItems: this.stringifyOtherCostItems(params.otherCostItemList, params.costClientHospitality),
        incomeMismatchReason: params.incomeMismatchReason,
        incomeMismatchRemark: params.incomeMismatchRemark,
        projectTarget: params.projectTarget,
        onsiteFeedbackStudent: params.onsiteFeedbackStudent,
        onsiteFeedbackClient: params.onsiteFeedbackClient,
        resultFeedbackSponsor: params.resultFeedbackSponsor,
        resultTeamChange: params.resultTeamChange,
        resultBusinessChange: params.resultBusinessChange,
    });

    buildStageList = (params: any): IProjectStage[] => {
        const stageList = (params.stageList || []) as any[];
        return stageList.map((item, index) => ({
            id: item.id,
            stageNo: item.id && Number(item.stageNo) > 0 ? Number(item.stageNo) : undefined,
            stageName: item.stageName || `阶段${index + 1}`,
            implementationTime: item.implementationTime ? item.implementationTime.format('YYYY-MM-DD') : undefined,
            implementationContent: item.implementationContent,
            executorName: item.executorName,
            deliverable: item.deliverable,
            paymentPlan: item.paymentPlan,
            status: item.status,
            sortNo: index + 1,
        }));
    };

    handleStageDragEnd = (result: DropResult) => {
        if (this.isViewMode()) {
            return;
        }
        const { source, destination } = result;
        if (!destination || source.index === destination.index) {
            return;
        }

        const currentList = (this.formRef.current?.getFieldValue('stageList') || []) as any[];
        if (!Array.isArray(currentList) || currentList.length === 0) {
            return;
        }

        const reordered = [...currentList];
        const [movedItem] = reordered.splice(source.index, 1);
        reordered.splice(destination.index, 0, movedItem);
        this.formRef.current?.setFieldsValue({ stageList: reordered });
    };

    onFinish = async (params: any) => {
        this.setState({ loading: true });

        if (!this.state.isEditMode) {
            const payload: ProjectSaveParam = {
                project: this.buildProjectPayload(params),
                stageList: this.buildStageList(params),
            };
            const response = await ProjectService.addProject(payload);
            if (response && response.success) {
                message.success(ADD_PROJECT_SUCCESS_MESSAGE);
                this.formRef.current?.resetFields();
                this.setState({
                    calculatedProjectProfit: '0.00',
                    calculatedProfitRate: '0.00',
                    showIncomeMismatchFields: false,
                });
            } else {
                message.error(response ? response.message : ADD_PROJECT_ERROR_MESSAGE);
            }
            this.setState({ loading: false });
            return;
        }

        const projectId = this.state.data!.id;
        const updateResponse = await ProjectService.updateProject({
            ...(this.buildProjectPayload(params) as ProjectUpdateParam),
            id: projectId,
        });

        if (!(updateResponse && updateResponse.success)) {
            message.error(updateResponse ? updateResponse.message : EDIT_PROJECT_ERROR_MESSAGE);
            this.setState({ loading: false });
            return;
        }

        // 阶段同步：有id则更新，无id则新增
        const stageList = this.buildStageList(params);
        for (const stage of stageList) {
            if (stage.id) {
                await ProjectService.updateStage(stage.id, stage);
            } else {
                await ProjectService.addStage({ ...stage, projectId });
            }
        }

        message.success(EDIT_PROJECT_SUCCESS_MESSAGE);
        this.setState({ loading: false });
        setTimeout(() => window.location.reload(), 500);
    };

    deleteStage = async (stageId: number, remove: () => void) => {
        if (!window.confirm(DELETE_PROJECT_STAGE_CONFIRM_MESSAGE)) {
            return;
        }
        const response = await ProjectService.deleteStage(stageId);
        if (response && response.success) {
            message.success('删除阶段成功');
            remove();
        } else {
            message.error(response ? response.message : '删除阶段失败');
        }
    };

    handleUpload = async (file: File) => {
        if (!this.state.data) {
            message.error('请先保存项目再上传附件');
            return;
        }
        const response = await ProjectService.uploadAttachment(this.state.data.id, file, undefined, 1);
        if (response && response.success) {
            message.success(UPLOAD_PROJECT_ATTACHMENT_SUCCESS_MESSAGE);
            const projectResponse = await ProjectService.getProjectById(this.state.data.id);
            this.setState({ attachmentList: projectResponse?.data?.projectInfo?.attachmentList || [] });
        } else {
            message.error(response ? response.message : UPLOAD_PROJECT_ATTACHMENT_ERROR_MESSAGE);
        }
    };

    deleteAttachment = async (attachmentId: number) => {
        const response = await ProjectService.deleteAttachment(attachmentId);
        if (response && response.success) {
            message.success('删除附件成功');
            this.setState({ attachmentList: this.state.attachmentList.filter(item => item.id !== attachmentId) });
        } else {
            message.error(response ? response.message : '删除附件失败');
        }
    };

    handleDownloadAllPlanAttachments = () => {
        const projectId = this.state.data?.id;
        if (!projectId) {
            message.info('项目信息不存在');
            return;
        }

        const hasPlanAttachment = (this.state.attachmentList || [])
            .some(item => item && item.attachmentType === 1);
        if (!hasPlanAttachment) {
            message.info('暂无可下载方案附件');
            return;
        }

        const downloadUrl = buildApiUrl(`/project/downloadAttachmentZip/${projectId}`);
        triggerDownload(downloadUrl);
    };

    getCurrentCompanyName = (): string => {
        const companyId = this.formRef.current?.getFieldValue('companyId');
        const fromOption = (this.state.companyOptions || []).find(option => Number(option.key) === Number(companyId));
        if (fromOption?.value) {
            return fromOption.value;
        }
        if (this.state.data?.companyName) {
            return this.state.data.companyName;
        }
        return '';
    };

    mapStageListToFormValues = (stageList: IProjectStage[]) => {
        return (stageList || []).map(item => ({
            id: item.id,
            stageNo: item.stageNo,
            stageName: item.stageName,
            implementationTime: item.implementationTime ? moment(item.implementationTime, 'YYYY-MM-DD') : undefined,
            implementationContent: item.implementationContent,
            executorName: item.executorDisplayName || item.executorName,
            deliverable: item.deliverable,
            paymentPlan: item.paymentPlan,
            status: item.status,
        }));
    };

    reloadStageListFromServer = async () => {
        const projectId = this.state.data?.id;
        if (!projectId) {
            return;
        }
        const response = await ProjectService.getProjectById(projectId);
        const latestStageList = response?.data?.projectInfo?.stageList || [];
        this.ensureStageExecutorOptions(latestStageList);
        this.formRef.current?.setFieldsValue({
            stageList: this.mapStageListToFormValues(latestStageList),
        });
    };

    handleImportPlanByAi = async () => {
        const projectId = this.state.data?.id;
        if (!projectId) {
            message.info('请先保存项目后再进行AI导入');
            return;
        }
        if (this.state.aiImportLoading) {
            return;
        }

        this.setState({ aiImportLoading: true });
        try {
            const companyName = this.getCurrentCompanyName();
            const response = await ProjectService.previewPlanByAi(projectId, companyName || undefined);
            if (response && response.success) {
                const currentStageCount = ((this.formRef.current?.getFieldValue('stageList') || []) as any[]).length;
                const previewStageList = (response?.data?.previewStageList || []).map((item: IAiPlanPreviewStage, index: number) => ({
                    ...item,
                    insertStageNo: currentStageCount + index + 1,
                }));
                this.setState({
                    aiPreviewVisible: true,
                    aiPreviewProjectTarget: response?.data?.projectTarget || '',
                    aiPreviewStageList: previewStageList,
                });
            } else {
                message.error(response ? response.message : 'AI解析失败');
            }
        } catch (e) {
            message.error('AI解析失败，请稍后重试');
        } finally {
            this.setState({ aiImportLoading: false });
        }
    };

    handleCloseAiPreview = () => {
        this.setState({
            aiPreviewVisible: false,
            aiPreviewStageList: [],
            aiPreviewProjectTarget: '',
            aiConfirmingPreviewIndex: null,
        });
    };

    updatePreviewInsertStageNo = (previewIndex: number, insertStageNo?: number) => {
        this.setState(prevState => ({
            aiPreviewStageList: (prevState.aiPreviewStageList || []).map(item => (
                item.previewIndex === previewIndex
                    ? { ...item, insertStageNo: insertStageNo === undefined ? undefined : Number(insertStageNo) }
                    : item
            )),
        }));
    };

    updatePreviewField = (
        previewIndex: number,
        field: 'stageName' | 'implementationTime' | 'implementationContent' | 'deliverable' | 'paymentPlan',
        value?: string,
    ) => {
        this.setState(prevState => ({
            aiPreviewStageList: (prevState.aiPreviewStageList || []).map(item => (
                item.previewIndex === previewIndex
                    ? { ...item, [field]: value }
                    : item
            )),
        }));
    };

    handleConfirmAiPreviewStage = async (record: IAiPlanPreviewStage) => {
        const projectId = this.state.data?.id;
        if (!projectId) {
            message.error('项目不存在');
            return;
        }
        const insertStageNo = Number(record.insertStageNo || 0);
        if (!Number.isFinite(insertStageNo) || insertStageNo <= 0) {
            message.error('请输入有效的插入序号');
            return;
        }

        this.setState({ aiConfirmingPreviewIndex: record.previewIndex });
        const response = await ProjectService.confirmAiPlanStage(projectId, {
            insertStageNo,
            stageName: record.stageName,
            implementationTime: record.implementationTime,
            implementationContent: record.implementationContent,
            deliverable: record.deliverable,
            paymentPlan: record.paymentPlan,
            projectTarget: this.state.aiPreviewProjectTarget,
        });

        if (response && response.success) {
            message.success('该阶段已插入项目计划');
            const remainingList = (this.state.aiPreviewStageList || [])
                .filter(item => item.previewIndex !== record.previewIndex);
            this.setState({ aiPreviewStageList: remainingList });
            await this.reloadStageListFromServer();
            if (remainingList.length === 0) {
                this.handleCloseAiPreview();
            }
        } else {
            message.error(response ? response.message : '确认插入失败');
        }
        this.setState({ aiConfirmingPreviewIndex: null });
    };

    loadStageOperationHistory = async () => {
        const projectId = this.state.data?.id;
        if (!projectId) {
            return;
        }
        this.setState({ stageHistoryLoading: true });
        const response = await ProjectService.getStageOperationHistory(projectId);
        if (response && response.success) {
            this.setState({ stageOperationHistoryList: response?.data?.operationHistoryList || [] });
        } else {
            message.error(response ? response.message : '获取阶段操作历史失败');
        }
        this.setState({ stageHistoryLoading: false });
    };

    handleOpenStageHistory = async () => {
        this.setState({ stageHistoryVisible: true });
        await this.loadStageOperationHistory();
    };

    handleCloseStageHistory = () => {
        this.setState({ stageHistoryVisible: false });
    };

    formatStageHistoryTime = (value?: number) => {
        if (!value) {
            return '-';
        }
        return moment(value).format('YYYY-MM-DD HH:mm:ss');
    };

    formatStageHistoryName = (record: IProjectStageOperationHistory) => {
        const stageNoText = record.stageNo ? `阶段${record.stageNo}` : '';
        const stageNameText = record.stageName ? `（${record.stageName}）` : '';
        const stageText = `${stageNoText}${stageNameText}`;
        return stageText || '-';
    };

    render() {
        const { isEditMode, data } = this.state;
        const isViewMode = this.isViewMode();
        const initialValues = {
            status: isEditMode && data ? data.status : ProjectStatus.waiting,
            stageList: isEditMode && data ? data.stageList || [] : [],
            otherCostItemList: isEditMode && data ? this.buildInitialOtherCostItems(data) : [],
            costClientHospitality: isEditMode && data ? this.getInitialClientHospitalityCost(data) : undefined,
        };
        const stageHistoryColumns = [
            {
                title: '操作时间',
                dataIndex: 'operationTime',
                key: 'operationTime',
                width: 170,
                render: (value: number) => this.formatStageHistoryTime(value),
            },
            {
                title: '操作人',
                dataIndex: 'operatorAccount',
                key: 'operatorAccount',
                width: 120,
                render: (value: string) => value || '-',
            },
            {
                title: '操作类型',
                dataIndex: 'operationType',
                key: 'operationType',
                width: 90,
                render: (value: string) => value || '-',
            },
            {
                title: '阶段',
                key: 'stageName',
                width: 180,
                render: (_: any, record: IProjectStageOperationHistory) => this.formatStageHistoryName(record),
            },
            {
                title: '变更说明',
                dataIndex: 'summary',
                key: 'summary',
                render: (value: string) => value || '-',
            },
        ];
        const aiPreviewColumns = [
            {
                title: '插入序号',
                key: 'insertStageNo',
                width: 82,
                render: (_: any, record: IAiPlanPreviewStage) => (
                    <InputNumber
                        min={1}
                        max={99}
                        style={{ width: 68 }}
                        value={record.insertStageNo}
                        onChange={(value) => this.updatePreviewInsertStageNo(record.previewIndex, value as number)}
                    />
                ),
            },
            {
                title: '阶段名称',
                dataIndex: 'stageName',
                key: 'stageName',
                width: 120,
                render: (_: string, record: IAiPlanPreviewStage) => (
                    <Input
                        value={record.stageName}
                        placeholder="阶段名称"
                        onChange={(e) => this.updatePreviewField(record.previewIndex, 'stageName', e.target.value)}
                    />
                ),
            },
            {
                title: '实施时间',
                dataIndex: 'implementationTime',
                key: 'implementationTime',
                width: 150,
                render: (_: string, record: IAiPlanPreviewStage) => (
                    <Input
                        value={record.implementationTime}
                        placeholder="YYYY-MM-DD"
                        onChange={(e) => this.updatePreviewField(record.previewIndex, 'implementationTime', e.target.value)}
                    />
                ),
            },
            {
                title: '实施内容',
                dataIndex: 'implementationContent',
                key: 'implementationContent',
                width: 250,
                render: (_: string, record: IAiPlanPreviewStage) => (
                    <Input.TextArea
                        autoSize={{ minRows: 1, maxRows: 3 }}
                        value={record.implementationContent}
                        placeholder="实施内容"
                        onChange={(e) => this.updatePreviewField(record.previewIndex, 'implementationContent', e.target.value)}
                    />
                ),
            },
            {
                title: '产出',
                dataIndex: 'deliverable',
                key: 'deliverable',
                width: 170,
                render: (_: string, record: IAiPlanPreviewStage) => (
                    <Input.TextArea
                        autoSize={{ minRows: 1, maxRows: 3 }}
                        value={record.deliverable}
                        placeholder="产出"
                        onChange={(e) => this.updatePreviewField(record.previewIndex, 'deliverable', e.target.value)}
                    />
                ),
            },
            {
                title: '回款计划',
                dataIndex: 'paymentPlan',
                key: 'paymentPlan',
                width: 210,
                render: (_: string, record: IAiPlanPreviewStage) => (
                    <Input.TextArea
                        autoSize={{ minRows: 1, maxRows: 3 }}
                        value={record.paymentPlan}
                        placeholder="回款计划"
                        onChange={(e) => this.updatePreviewField(record.previewIndex, 'paymentPlan', e.target.value)}
                    />
                ),
            },
            {
                title: '操作',
                key: 'action',
                width: 90,
                render: (_: any, record: IAiPlanPreviewStage) => (
                    <Button
                        type="primary"
                        size="small"
                        loading={this.state.aiConfirmingPreviewIndex === record.previewIndex}
                        onClick={() => this.handleConfirmAiPreviewStage(record)}
                    >
                        确认
                    </Button>
                ),
            },
        ];

        return (
            <div className={`page-project_container ${isViewMode ? 'project-view-mode' : ''}`}>
                <BreadcrumbCustom breads={['项目管理', isViewMode ? '查看项目' : (isEditMode ? '编辑项目' : '新建项目')]} />
                <Card
                    title={
                        <div className="title-manager_page">
                            <Button type="link" className="back-button" onClick={this.handleBackButtonClick}>{'< 返回'}</Button>
                            {isViewMode ? '查看项目' : (isEditMode ? '编辑项目' : '新建项目')}
                        </div>
                    }
                    bordered={false}
                >
                    <div className="project-form_container">
                        <Form
                            ref={this.formRef}
                            onFinish={this.onFinish}
                            initialValues={initialValues}
                            onValuesChange={(_, allValues) => this.calculateAndSetProfitInfo(allValues)}
                        >
                            <Tabs defaultActiveKey="basic" className="project-tabs">
                                <Tabs.TabPane tab="基本信息" key="basic">
                                    <Form.Item label="项目名称" name="name" className="formItem-project" rules={[{ required: true, message: '请输入项目名称' }]}>
                                        <Input placeholder="请输入项目名称" />
                                    </Form.Item>

                                    <Form.Item label="项目起止日期" className="formItem-project_date">
                                        <Space>
                                            <Form.Item name="startDate" noStyle>
                                                <DatePicker placeholder="开始日期" format="YYYY-MM-DD" />
                                            </Form.Item>
                                            <span>至</span>
                                            <Form.Item name="endDate" noStyle>
                                                <DatePicker placeholder="结束日期" format="YYYY-MM-DD" />
                                            </Form.Item>
                                        </Space>
                                    </Form.Item>

                                    <Form.Item label="产品服务类别" name="serviceCategory" className="formItem-project_select">
                                        <Select allowClear={!isViewMode} placeholder="请选择产品服务类别">
                                            {PROJECT_SERVICE_CATEGORY_OPTIONS.map(item => (
                                                <Select.Option key={item} value={item}>{item}</Select.Option>
                                            ))}
                                        </Select>
                                    </Form.Item>

                                    <Form.Item label="客户名称" name="companyId" className="formItem-project_select" rules={[{ required: true, message: '请选择客户公司' }]}>
                                        <Select allowClear={!isViewMode} showSearch optionFilterProp="children" placeholder="请选择客户公司">
                                            {(this.state.companyOptions || []).map(option => (
                                                <Select.Option key={option.key} value={option.key}>{option.value}</Select.Option>
                                            ))}
                                        </Select>
                                    </Form.Item>

                                    <Form.Item label="客户经理" name="customerManagerId" className="formItem-project_select" rules={[{ required: true, message: '请选择客户经理' }]}>
                                        <Select allowClear={!isViewMode} showSearch optionFilterProp="children" placeholder="请选择客户经理">
                                            {(this.state.customerManagerOptions || []).map(option => (
                                                <Select.Option key={option.key} value={option.key}>{option.value}</Select.Option>
                                            ))}
                                        </Select>
                                    </Form.Item>

                                    <Form.Item label="项目经理" name="projectManagerAdminId" className="formItem-project_select">
                                        <ProjectManagerSelect
                                            allowClear={!isViewMode}
                                            placeholder="请选择项目经理"
                                            disabled={isViewMode}
                                        />
                                    </Form.Item>

                                    <Form.Item
                                        label="项目合同金额"
                                        name="contractAmount"
                                        className="formItem-project_small"
                                        getValueFromEvent={(e) => this.sanitizeAmountInput(e.target.value)}
                                    >
                                        <Input placeholder="请输入金额" />
                                    </Form.Item>

                                    <Form.Item label="项目状态" name="status" className="formItem-project_status">
                                        <ProjectStatusSelect />
                                    </Form.Item>

                                    <Card size="small" title="项目实施计划（阶段）" className="card-project_stage">
                                        <Spin spinning={this.state.aiImportLoading} tip="AI项目方案解读中，请稍候...">
                                            <div className="project-stage-header">
                                                {!isViewMode && <span className="stage-drag-handle stage-header-drag-placeholder" />}
                                                <span className="stage-header-cell stage-header-index">阶段</span>
                                                <span className="stage-header-cell stage-field-name">阶段名称</span>
                                                <span className="stage-header-cell stage-field-time">实施时间</span>
                                                <span className="stage-header-cell stage-field-content">实施内容</span>
                                                <span className="stage-header-cell stage-field-executor">实施人</span>
                                                <span className="stage-header-cell stage-field-deliverable">产出</span>
                                                <span className="stage-header-cell stage-field-payment">回款计划</span>
                                                <span className="stage-header-cell stage-field-status">状态</span>
                                                {!isViewMode && <span className="stage-header-cell stage-header-delete-placeholder" />}
                                            </div>
                                            <Form.List name="stageList">
                                                {(fields, { add, remove }) => (
                                                    <>
                                                        <DragDropContext onDragEnd={this.handleStageDragEnd}>
                                                            <Droppable droppableId="project-stage-list">
                                                                {(dropProvided) => (
                                                                    <div ref={dropProvided.innerRef} {...dropProvided.droppableProps}>
                                                                        {fields.map((field, index) => {
                                                                            const stageId = this.formRef.current?.getFieldValue(['stageList', field.name, 'id']);
                                                                            return (
                                                                                <Draggable
                                                                                    key={field.key}
                                                                                    draggableId={`project-stage-${String(field.key)}`}
                                                                                    index={index}
                                                                                    isDragDisabled={isViewMode}
                                                                                >
                                                                                    {(dragProvided) => (
                                                                                        <div
                                                                                            className="project-stage-row"
                                                                                            ref={dragProvided.innerRef}
                                                                                            {...dragProvided.draggableProps}
                                                                                        >
                                                                                            <Space align="start" size={4} className="project-stage-space">
                                                                                            <Form.Item {...field} name={[field.name, 'id']} hidden>
                                                                                                <Input type="hidden" />
                                                                                            </Form.Item>
                                                                                            <Form.Item {...field} name={[field.name, 'stageNo']} hidden>
                                                                                                <Input type="hidden" />
                                                                                            </Form.Item>
                                                                                            {!isViewMode && (
                                                                                                <span className="stage-drag-handle" {...dragProvided.dragHandleProps}>
                                                                                                    <DragOutlined />
                                                                                                </span>
                                                                                            )}
                                                                                            <span className="stage-index-label">{`阶段${index + 1}`}</span>
                                                                                            <Form.Item {...field} name={[field.name, 'stageName']} className="stage-field-name">
                                                                                                <Input placeholder="阶段名称" />
                                                                                            </Form.Item>
                                                                                            <Form.Item {...field} name={[field.name, 'implementationTime']} className="stage-field-time">
                                                                                                <DatePicker format="YYYY-MM-DD" placeholder="实施时间" />
                                                                                            </Form.Item>
                                                                                            <Form.Item {...field} name={[field.name, 'implementationContent']} className="stage-field-content">
                                                                                                <Input.TextArea
                                                                                                    autoSize={{ minRows: 1 }}
                                                                                                    placeholder="实施内容"
                                                                                                />
                                                                                            </Form.Item>
                                                                                            <Form.Item {...field} name={[field.name, 'executorName']} className="stage-field-executor">
                                                                                                <Select
                                                                                                    allowClear={!isViewMode}
                                                                                                    showSearch
                                                                                                    optionFilterProp="children"
                                                                                                    placeholder="请选择实施人"
                                                                                                >
                                                                                                    {(this.state.stageExecutorOptions || []).map((item) => (
                                                                                                        <Select.Option key={item} value={item}>{item}</Select.Option>
                                                                                                    ))}
                                                                                                </Select>
                                                                                            </Form.Item>
                                                                                            <Form.Item {...field} name={[field.name, 'deliverable']} className="stage-field-deliverable">
                                                                                                <Input placeholder="产出" />
                                                                                            </Form.Item>
                                                                                            <Form.Item {...field} name={[field.name, 'paymentPlan']} className="stage-field-payment">
                                                                                                <Input placeholder="回款计划" />
                                                                                            </Form.Item>
                                                                                            <Form.Item {...field} name={[field.name, 'status']} className="stage-field-status">
                                                                                                <ProjectStageStatusSelect placeholder="状态" />
                                                                                            </Form.Item>
                                                                                            {!isViewMode && (stageId ? (
                                                                                                <Button
                                                                                                    className="stage-delete-btn"
                                                                                                    icon={<MinusCircleOutlined />}
                                                                                                    onClick={() => this.deleteStage(stageId, () => remove(field.name))}
                                                                                                />
                                                                                            ) : (
                                                                                                <Button className="stage-delete-btn" icon={<MinusCircleOutlined />} onClick={() => remove(field.name)} />
                                                                                            ))}
                                                                                            </Space>
                                                                                        </div>
                                                                                    )}
                                                                                </Draggable>
                                                                            );
                                                                        })}
                                                                        {dropProvided.placeholder}
                                                                    </div>
                                                                )}
                                                            </Droppable>
                                                        </DragDropContext>
                                                        {isViewMode && fields.length === 0 && (
                                                            <div className="project-stage-empty">
                                                                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无数据" />
                                                            </div>
                                                        )}
                                                        {!isViewMode && (
                                                            <div className="project-stage-actions">
                                                                <Button type="dashed" onClick={() => add()} icon={<PlusOutlined />}>添加阶段</Button>
                                                                {isEditMode && (
                                                                    <Button
                                                                        className="project-ai-import-btn"
                                                                        onClick={this.handleImportPlanByAi}
                                                                        disabled={this.state.aiImportLoading}
                                                                    >
                                                                        AI项目方案解读
                                                                    </Button>
                                                                )}
                                                                {isEditMode && (
                                                                    <Button onClick={this.handleOpenStageHistory}>项目计划操作历史</Button>
                                                                )}
                                                            </div>
                                                        )}
                                                    </>
                                                )}
                                            </Form.List>
                                            {isViewMode && (
                                                <div className="project-stage-history-toolbar">
                                                    <Button onClick={this.handleOpenStageHistory}>项目计划操作历史</Button>
                                                </div>
                                            )}
                                        </Spin>
                                    </Card>

                                    <Form.Item label="项目方案附件" className="formItem-project_attachment">
                                        {!isViewMode && (
                                            <Upload
                                                multiple
                                                beforeUpload={(file) => {
                                                    this.handleUpload(file as unknown as File);
                                                    return false;
                                                }}
                                                showUploadList={false}
                                            >
                                                <Button icon={<UploadOutlined />}>上传附件</Button>
                                            </Upload>
                                        )}

                                        <List
                                            className="project-attachment-list"
                                            size="small"
                                            dataSource={this.state.attachmentList}
                                            renderItem={(item) => (
                                                <List.Item
                                                    actions={(isViewMode
                                                        ? [
                                                            <a key="open" href={buildFileUrl(item.fileUrl)} target="_blank" rel="noopener noreferrer">查看</a>,
                                                        ]
                                                        : [
                                                            <a key="open" href={buildFileUrl(item.fileUrl)} target="_blank" rel="noopener noreferrer">查看</a>,
                                                            <button key="delete" type="button" onClick={() => this.deleteAttachment(item.id)}>删除</button>,
                                                        ])}
                                                >
                                                    {item.fileName}
                                                </List.Item>
                                            )}
                                        />
                                        {isViewMode && (this.state.attachmentList || []).some(item => item && item.attachmentType === 1) && (
                                            <div className="project-attachment-download-all">
                                                <button
                                                    type="button"
                                                    className="link-button"
                                                    onClick={this.handleDownloadAllPlanAttachments}
                                                >
                                                    一键下载方案附件
                                                </button>
                                            </div>
                                        )}
                                    </Form.Item>
                                </Tabs.TabPane>

                                <Tabs.TabPane tab="损益分析" key="profit">
                                    <Card size="small" title="损益分析" className="card-project_profit">
                                        <Form.Item label="项目实际收入" name="actualIncome" className="formItem-project_small" getValueFromEvent={(e) => this.sanitizeAmountInput(e.target.value)}><Input /></Form.Item>
                                        <Form.Item label="顾问费用" name="costConsultantFee" className="formItem-project_small" getValueFromEvent={(e) => this.sanitizeAmountInput(e.target.value)}><Input /></Form.Item>
                                        <Form.Item label="顾问差旅" name="costConsultantTravel" className="formItem-project_small" getValueFromEvent={(e) => this.sanitizeAmountInput(e.target.value)}><Input /></Form.Item>
                                        <Form.Item label="顾问餐食" name="costConsultantMeal" className="formItem-project_small" getValueFromEvent={(e) => this.sanitizeAmountInput(e.target.value)}><Input /></Form.Item>
                                        <Form.Item label="学员教材" name="costMaterial" className="formItem-project_small" getValueFromEvent={(e) => this.sanitizeAmountInput(e.target.value)}><Input /></Form.Item>
                                        <Form.Item label="教具" name="costTeachingAid" className="formItem-project_small" getValueFromEvent={(e) => this.sanitizeAmountInput(e.target.value)}><Input /></Form.Item>
                                        <Form.Item label="茶歇" name="costTeaBreak" className="formItem-project_small" getValueFromEvent={(e) => this.sanitizeAmountInput(e.target.value)}><Input /></Form.Item>
                                        <Form.Item label={FIXED_COST_CLIENT_HOSPITALITY} name="costClientHospitality" className="formItem-project_small" getValueFromEvent={(e) => this.sanitizeAmountInput(e.target.value)}><Input /></Form.Item>
                                        <Form.Item label="学员奖品" name="costPrize" className="formItem-project_small" getValueFromEvent={(e) => this.sanitizeAmountInput(e.target.value)}><Input /></Form.Item>
                                        <Form.Item label="项目经理差旅" name="costPmTravel" className="formItem-project_small" getValueFromEvent={(e) => this.sanitizeAmountInput(e.target.value)}><Input /></Form.Item>
                                        <Form.Item label="增值税费" name="costVat" className="formItem-project_small" getValueFromEvent={(e) => this.sanitizeAmountInput(e.target.value)}><Input /></Form.Item>

                                        <Form.Item label="其他成本项" className="formItem-project_text formItem-project_other_cost">
                                            <Form.List name="otherCostItemList">
                                                {(fields, { add, remove }) => (
                                                    <>
                                                        {fields.map((field) => (
                                                            <Space key={field.key} align="baseline" style={{ display: 'flex', marginBottom: 8 }} className="project-other-cost-row">
                                                                <Form.Item
                                                                    {...field}
                                                                    name={[field.name, 'name']}
                                                                    rules={[{ required: true, message: '请输入成本名称' }]}
                                                                    className="project-other-cost-name"
                                                                >
                                                                    <Input placeholder="成本名称" />
                                                                </Form.Item>
                                                                <Form.Item
                                                                    {...field}
                                                                    name={[field.name, 'amount']}
                                                                    className="project-other-cost-amount"
                                                                    getValueFromEvent={(e) => this.sanitizeAmountInput(e.target.value)}
                                                                >
                                                                    <Input placeholder="金额" />
                                                                </Form.Item>
                                                                {!isViewMode && (
                                                                    <MinusCircleOutlined
                                                                        onClick={() => {
                                                                            remove(field.name);
                                                                            setTimeout(() => this.calculateAndSetProfitInfo(this.formRef.current?.getFieldsValue() || {}), 0);
                                                                        }}
                                                                    />
                                                                )}
                                                            </Space>
                                                        ))}
                                                        {!isViewMode && (
                                                            <Form.Item className="project-other-cost-add">
                                                                <Button
                                                                    type="dashed"
                                                                    onClick={() => {
                                                                        add({ name: '', amount: '' });
                                                                        setTimeout(() => this.calculateAndSetProfitInfo(this.formRef.current?.getFieldsValue() || {}), 0);
                                                                    }}
                                                                    icon={<PlusOutlined />}
                                                                >
                                                                    新增其他成本项
                                                                </Button>
                                                            </Form.Item>
                                                        )}
                                                    </>
                                                )}
                                            </Form.List>
                                        </Form.Item>

                                        {this.state.showIncomeMismatchFields && (
                                            <>
                                                <Form.Item
                                                    label="收入不一致原因"
                                                    name="incomeMismatchReason"
                                                    className="formItem-project_text"
                                                    rules={[{ required: true, message: '实际收入与合同金额不一致时，请填写原因' }]}
                                                >
                                                    <Input placeholder="请输入原因说明" />
                                                </Form.Item>
                                                <Form.Item
                                                    label="收入不一致备注"
                                                    name="incomeMismatchRemark"
                                                    className="formItem-project_text"
                                                >
                                                    <Input.TextArea rows={2} placeholder="可填写补充说明" />
                                                </Form.Item>
                                            </>
                                        )}

                                        <Form.Item label="项目利润(自动计算)" className="formItem-project_small">
                                            <Input value={this.state.calculatedProjectProfit} disabled />
                                        </Form.Item>
                                        <Form.Item label="利润率%(自动计算)" className="formItem-project_small">
                                            <Input value={this.state.calculatedProfitRate} disabled />
                                        </Form.Item>
                                    </Card>
                                </Tabs.TabPane>

                                <Tabs.TabPane tab="效果跟踪" key="tracking">
                                    <Card size="small" title="效果跟踪" className="card-project_effect">
                                        <Form.Item label="项目目标" name="projectTarget" className="formItem-project_text"><Input.TextArea rows={3} /></Form.Item>
                                        <Form.Item label="现场反馈-学员评估" name="onsiteFeedbackStudent" className="formItem-project_text"><Input.TextArea rows={3} /></Form.Item>
                                        <Form.Item label="现场反馈-客户对接人" name="onsiteFeedbackClient" className="formItem-project_text"><Input.TextArea rows={3} /></Form.Item>
                                        <Form.Item label="实施后-项目发起人" name="resultFeedbackSponsor" className="formItem-project_text"><Input.TextArea rows={3} /></Form.Item>
                                        <Form.Item label="实施后-学员/团队变化" name="resultTeamChange" className="formItem-project_text"><Input.TextArea rows={3} /></Form.Item>
                                        <Form.Item label="实施后-业务变化" name="resultBusinessChange" className="formItem-project_text"><Input.TextArea rows={3} /></Form.Item>
                                    </Card>
                                </Tabs.TabPane>
                            </Tabs>

                            {!isViewMode && (
                                <Form.Item>
                                    <Button type="primary" className="button-project_submit" htmlType="submit" loading={this.state.loading}>
                                        提交
                                    </Button>
                                </Form.Item>
                            )}
                        </Form>
                    </div>
                </Card>
                <Modal
                    title="AI解析预览（逐条确认插入）"
                    visible={this.state.aiPreviewVisible}
                    onCancel={this.handleCloseAiPreview}
                    footer={null}
                    width={1300}
                    wrapClassName="ai-preview-modal"
                    destroyOnClose
                >
                    <div className="ai-preview-target">
                        <span className="ai-preview-target-label">识别的项目目标：</span>
                        <span className="ai-preview-target-content">{this.state.aiPreviewProjectTarget || '（空）'}</span>
                    </div>
                    <Table<IAiPlanPreviewStage>
                        rowKey={(record) => String(record.previewIndex)}
                        dataSource={this.state.aiPreviewStageList}
                        columns={aiPreviewColumns as any}
                        pagination={false}
                        locale={{ emptyText: '暂无待确认数据' }}
                        tableLayout="fixed"
                    />
                </Modal>
                <Modal
                    title="项目实施计划操作历史"
                    visible={this.state.stageHistoryVisible}
                    onCancel={this.handleCloseStageHistory}
                    footer={null}
                    width={1030}
                    destroyOnClose
                >
                    <Table<IProjectStageOperationHistory>
                        rowKey={(record) => String(record.logId)}
                        loading={this.state.stageHistoryLoading}
                        dataSource={this.state.stageOperationHistoryList}
                        columns={stageHistoryColumns as any}
                        pagination={{ pageSize: 10 }}
                        locale={{ emptyText: '暂无操作历史' }}
                    />
                </Modal>
            </div>
        );
    }
}

export default ProjectManagerPage;
